import React, { useState, useEffect, useMemo, useRef } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';

import DownloadIcon from 'apps/web_forms/assets/download-icon.svg';
import CloseIcon from 'apps/web_forms/assets/close.svg';
import PdfIcon from 'apps/web_forms/assets/filetypes/pdf.svg';
import PngIcon from 'apps/web_forms/assets/filetypes/png.svg';
import JpgIcon from 'apps/web_forms/assets/filetypes/jpg.svg';
import TxtIcon from 'apps/web_forms/assets/filetypes/txt.svg';
import UnknownFileIcon from 'apps/web_forms/assets/filetypes/unknown.svg';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import useRules from 'apps/web_forms/hooks/useRules';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import {
  fetchResourceInfo,
  fetchResourceData,
  cancelResourceRequest,
} from 'apps/web_forms/slices/resourceSlice';
import { COLORS } from 'apps/web_forms/utils/constants';

import { StyledResourceContainer } from './styles';

// "Attached File"
const ResourceInput = ({ element, formContext, showContent }) => {
  const [downloading, setDownloading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const downloadTriggerRef = useRef(null);
  const dispatch = useDispatch();
  const { identifier, title, hint, resource_identifier: resourceId } = element;
  
  const currentResource = useSelector((state) => state.resource.resourcesMap[resourceId]);
  const currentSelectedMultimediaInput = useSelector((state) => state.multimedia.currentSelectedMultimediaInput);
  const resourceVersion = useSelector((state) => state.form.resourceVersions?.[resourceId]);

  const [{ isActive }] = useRules(element, formContext);

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const FileTypeIcon = useMemo(() => {
    switch (currentResource?.mimeType) {
      case 'application/pdf':
        return PdfIcon;
      case 'image/png':
        return PngIcon;
      case 'image/jpeg':
        return JpgIcon;
      case 'text/plain':
        return TxtIcon;
      default:
        return UnknownFileIcon;
    }
  }, [currentResource?.mimeType]);

  const fileExtension = useMemo(() => {
    switch (currentResource?.mimeType) {
      case 'application/pdf':
        return 'pdf';
      case 'image/png':
        return 'png';
      case 'image/jpeg':
        return 'jpg';
      case 'text/plain':
        return 'txt';
      default:
        return '';
    }
  }, [currentResource?.mimeType]);

  useEffect(() => {
    if (currentSelectedMultimediaInput) setLoadingPreview(false);

    if (currentSelectedMultimediaInput?.element !== element && !downloading) {
      cancelResourceRequest(resourceId);
    }
  }, [currentSelectedMultimediaInput]);

  useEffect(() => {
    if (currentResource?.url) {
      setLoadingPreview(false);
    }

    // Handle download
    if (downloading && !currentResource?.url) triggerResourceFetch();

    if (downloading && currentResource?.url) {
      downloadTriggerRef.current.href = currentResource?.url;
      downloadTriggerRef.current.click();
      setDownloading(false);
    }
  }, [downloading, currentResource]);

  useEffect(() => {
    dispatch(fetchResourceInfo(resourceId));
  }, [resourceId]);

  const triggerResourceFetch = () => {
    dispatch(fetchResourceData(resourceId, resourceVersion));
  };

  const onClickFileInfo = () => {
    triggerResourceFetch();
    dispatch(setCurrentSelectedMultimediaInput({ element }));
    setLoadingPreview(true);
  };

  const cancelDownload = () => {
    setDownloading(false);

    if (!loadingPreview) cancelResourceRequest(resourceId);
  };

  if (!isActive || !showContent) {
    return null;
  }

  return (
    <div className='react-form-control'>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label>{title}</label>
      {currentResource && (
        <StyledResourceContainer>
          <div
            onClick={onClickFileInfo}
            onFocus={removeResourceUpdateHighlight}
            id={`file-info-${identifier}`}
            className={clsx(
              'file-info',
              hasResourceUpdateHighlight && 'updateHighlight'
            )}
            allow-enter='true'
            onKeyPress={(e) => e.which === 13 && onClickFileInfo()}
            role='button'
            tabIndex={0}
            data-testid='file-info'
          >
            <FileTypeIcon />
            <span>{currentResource.fileName}</span>
          </div>

          {downloading ? (
            <div className='downloading-container'>
              <button
                onClick={() => cancelDownload()}
                type='button'
                data-testid='cancel-download-button'
              >
                <Loader
                  type='Oval'
                  color={COLORS.primary}
                  height='16em'
                  width={100}
                />
                <CloseIcon className='close-icon' />
              </button>
            </div>
          ) : (
            <div
              className={clsx(
                'download-icon-container',
                hasResourceUpdateHighlight && 'updateHighlight'
              )}
              title='Download file'
            >
              <button
                onClick={() => setDownloading(true)}
                onFocus={removeResourceUpdateHighlight}
                id={identifier}
                name={identifier}
                type='button'
                data-testid='download-button'
              >
                <DownloadIcon />
              </button>
            </div>
          )}

          {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
          <a
            download={`${currentResource.fileName}.${fileExtension}`}
            ref={downloadTriggerRef}
            hidden
          />
          {hint && <p className='element-hint'>{hint}</p>}
        </StyledResourceContainer>
      )}
    </div>
  );
};

ResourceInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    resource_identifier: PropTypes.string,
    hint: PropTypes.string,
  }).isRequired,
};

export default React.memo(ResourceInput);
